import { VNode } from "vue";
import { EventHandler, EventHandlerAsync, ValidateEventArgs, VisibilityChangedEventArgs } from "..";
import { Control } from "../controls";

export abstract class Modal<T = undefined, TResult = void> {
    protected _visible: boolean = false;
    protected _resultEvent: Function | null = null;
    protected _errorEvent: Function | null = null;

    public readonly id: string;
    public size: "xs" | "sm" | "md" | "lg" | "xl" | "fit" = "sm";
    public title: string;
    public titleVNode: VNode | null = null;

    public constructor(id: string, title = "") {
        this.id = id;
        this.title = title;
    }

    public get submodals(): Modal<any, any>[] {
        return [];
    }

    public abstract get controls(): Control[];

    //

    public show(data?: T): Promise<TResult> {
        this._visible = true;
        this.notifyVisibleChangedHandlers();

        return new Promise((resolve: any, reject: any) => {
            this._resultEvent = (result: TResult) => resolve(result);
            this._errorEvent = (e: Error) => reject(e);
        });
    }

    public return<TResult>(result?: TResult): void {
        if (this._resultEvent) {
            this._resultEvent(result);
            this._resultEvent = null;
        }
    }

    public hide<TResult>(result?: TResult): void {
        this.return(result);

        this._visible = false;
        this.notifyVisibleChangedHandlers();
    }

    private _visibleChangedHandlers = new Set<EventHandler<VisibilityChangedEventArgs>>();
    public addVisibleChangedHandler(handler: EventHandler<VisibilityChangedEventArgs>): void {
        this._visibleChangedHandlers.add(handler);
    }
    public removeVisibleChangedHandler(handler: EventHandler<VisibilityChangedEventArgs>): void {
        this._visibleChangedHandlers.delete(handler);
    }
    private notifyVisibleChangedHandlers(): void {
        const args: VisibilityChangedEventArgs = { visible: this._visible };
        for (const handler of this._visibleChangedHandlers) {
            handler(this, args);
        }
    }

    //

    public async validate(): Promise<boolean> {
        return await this.notifyValidateHandlers();
    }

    private _validateHandlers = new Set<EventHandlerAsync<ValidateEventArgs>>();
    public addValidateHandler(handler: EventHandlerAsync<ValidateEventArgs>): void {
        this._validateHandlers.add(handler);
    }
    public removeValidateHandler(handler: EventHandlerAsync<ValidateEventArgs>): void {
        this._validateHandlers.delete(handler);
    }
    private async notifyValidateHandlers(): Promise<boolean> {
        const args: ValidateEventArgs = { valid: true };
        for (const handler of this._validateHandlers) {
            await handler(this, args);
            if (!args.valid) {
                return false;
            }
        }
        return true;
    }
}
